import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Zap } from "lucide-react";
import { Heading, Text } from "@/components/Typography";
import { Button } from "@/components/Button";
import { api } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import { MatchedAdvisor } from "@/types/lead";
import { AILoadingAnimation } from "@/components/AILoadingAnimation";
import { PageHeaderLogo } from "@/components/PageHeaderLogo";
import { AdvisorCard } from "./components/AdvisorCard";
import * as Tooltip from "@radix-ui/react-tooltip";
import { leadMatchingComplete, leadBookDemoClicked, leadMatchingLimitReached } from "@/utils/tracking";

export const LeadMatchingPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { showSuccessToast, showErrorToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [bookingDemo, setBookingDemo] = useState(false);
  const [advisors, setAdvisors] = useState<MatchedAdvisor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isExhausted, setIsExhausted] = useState(false);
  const matchingStartTime = useRef<number>(Date.now());
  const pageLoadTime = useRef<number>(Date.now());

  // Convert userId from URL params to number (it's actually leadId)
  const leadId = userId ? parseInt(userId, 10) : null;

  useEffect(() => {
    const matchAdvisors = async () => {
      if (!leadId) {
        setError("Invalid lead ID");
        setLoading(false);
        return;
      }

      try {
        const response = await api.leads.matchWithAdvisors(leadId, 9);
        
        // Debug logging
        console.log("API Response:", response);
        console.log("Matched Advisors:", response.matchedAdvisors);

        // Handle the response
        const matchedAdvisors = response.matchedAdvisors || [];
        
        if (!Array.isArray(matchedAdvisors)) {
          console.error("Invalid response structure:", response);
          throw new Error("Invalid response structure from server");
        }

        setAdvisors(matchedAdvisors);
        setLoading(false);

        // Track matching complete - only once per lead
        const trackingKey = `lead_matching_tracked_${leadId}`;
        const alreadyTracked = localStorage.getItem(trackingKey);

        if (!alreadyTracked) {
          const topMatchAdvisor = matchedAdvisors.find((a) => a.topMatch);
          const matchingDurationSeconds = Math.floor(
            (Date.now() - matchingStartTime.current) / 1000
          );

          leadMatchingComplete({
            lead_id: leadId,
            advisors_count: matchedAdvisors.length,
            top_match_score: topMatchAdvisor?.matchScore || 0,
            matching_duration_seconds: matchingDurationSeconds,
            has_top_match: !!topMatchAdvisor,
          });

          // Mark as tracked
          localStorage.setItem(trackingKey, "true");
        }

        showSuccessToast(
          `We found ${matchedAdvisors.length} perfect advisors for you!`,
          "Advisors Matched!"
        );
      } catch (err: any) {
        console.error("Matching error:", err);
        console.error("Error response:", err.response?.data);

        // Check if it's a rate limit error (handled by axios interceptor)
        if (err.isRateLimitError) {
          setIsExhausted(true);
          setLoading(false);

          // Track matching limit reached
          if (leadId) {
            leadMatchingLimitReached({
              lead_id: leadId,
              max_attempts: 3,
            });
          }
          return;
        }

        const errorMessage = err.response?.data?.detail || err.message || "Failed to match advisors. Please try again.";
        setError(errorMessage);
        setLoading(false);

        showErrorToast(errorMessage, "Matching Failed");
      }
    };

    matchAdvisors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const handleBookDemoCall = async (
    source: "matching_page" | "matching_limit_screen"
  ) => {
    if (!leadId) return;

    // Track demo call click
    const timeOnPageSeconds = Math.floor((Date.now() - pageLoadTime.current) / 1000);
    leadBookDemoClicked({
      lead_id: leadId,
      click_source: source,
      advisors_viewed_count: advisors.length,
      time_on_page_seconds: timeOnPageSeconds,
    });

    setBookingDemo(true);
    try {
      const response = await api.leads.bookDemoCall(leadId);

      showSuccessToast("Taking you to our booking page...", "Redirecting to HubSpot");

      // Redirect to HubSpot booking page
      setTimeout(() => {
        window.location.href = response.hubspotBookingUrl;
      }, 1000);
    } catch (err: any) {
      console.error("Booking error:", err);
      const errorMessage = err.response?.data?.detail || err.message || "Failed to book demo call";
      showErrorToast(errorMessage, "Booking Failed");
      setBookingDemo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeaderLogo />
        <AILoadingAnimation message="Analyzing your goals and AI matching you with expert advisors!" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeaderLogo />
        <div className="w-full flex-1 flex justify-center items-center bg-gray-50">
          <div className="w-full max-w-md flex flex-col items-center gap-4 p-6">
            <div className="text-6xl">❌</div>
            <Heading size="6" as="h1" className="text-center font-bold text-red-600">
              Matching Failed
            </Heading>
            <Text size="4" as="p" className="text-center text-gray-600">
              {error}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  if (isExhausted) {
    return (
      <div className="flex flex-col min-h-screen">
        <PageHeaderLogo />
        <div className="w-full flex-1 flex justify-center items-center bg-gray-50">
          <div className="w-full max-w-lg flex flex-col items-center gap-6 p-8 bg-white rounded-xl shadow-lg">
            <div className="text-6xl">🎯</div>
            <Heading size="6" as="h1" className="text-center font-bold text-gray-900">
              You've Reached Your Matching Limit
            </Heading>
            <Text size="4" as="p" className="text-center text-gray-600 leading-relaxed">
              You've already used your 3 free advisor matches. To get more
              personalized recommendations, book a demo call with our team!
            </Text>
            <div className="flex flex-col items-center gap-3 mt-4">
              <Button
                size="lg"
                onClick={() => handleBookDemoCall("matching_limit_screen")}
                disabled={bookingDemo}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg"
              >
                {bookingDemo ? "Redirecting..." : "📞 Book a Demo Call"}
              </Button>
              <Text size="3" as="p" className="text-gray-500">
                Our team will help you find the perfect advisors
              </Text>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Tooltip.Provider delayDuration={200}>
      <div className="flex flex-col min-h-screen">
        <PageHeaderLogo />

        {/* Main Content - with bottom padding for sticky footer */}
        <div className="flex-1 w-full max-w-7xl mx-auto px-8 py-8 pb-40">
          {/* Header Section */}
          <div className="flex justify-between items-start mb-16">
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-4">
                <Heading as="h1" size="4" className="font-bold">
                  Meet Your Top Leadership Advisors
                </Heading>
                <div className="flex items-center gap-2 bg-primary-50 text-primary-600 px-4 py-2 rounded text-xs font-medium">
                  <Zap size={12} />
                  POWERED BY AI
                </div>
              </div>
              <Text as="p" size="4" className="text-gray-600">
                Based on your goals and experience, we've matched you with{" "}
                {advisors.length} expert advisors
              </Text>
            </div>

            {/* Right side - Book Demo Button */}
            <div className="flex flex-col items-end gap-4 ml-16">
              <Button
                size="lg"
                onClick={() => handleBookDemoCall("matching_page")}
                disabled={bookingDemo}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg"
              >
                {bookingDemo ? "Redirecting..." : "📞 Book a Call with Us"}
              </Button>
              <Text size="3" as="p" className="text-gray-500">
                Talk to our team to get started
              </Text>
            </div>
          </div>

          {/* Advisor Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
            {advisors.map((advisor) => (
              <AdvisorCard key={advisor.id} advisor={advisor} leadId={leadId!} />
            ))}
          </div>
        </div>

        {/* Sticky Footer with Book Demo Button */}
        <div
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10"
          style={{ height: "100px" }}
        >
          <div className="w-full max-w-7xl mx-auto px-8 h-full flex items-center justify-center">
            <div className="text-center">
              <Button
                size="lg"
                onClick={() => handleBookDemoCall("matching_page")}
                disabled={bookingDemo}
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 font-semibold rounded-full"
              >
                {bookingDemo ? "Redirecting..." : "Book Your Demo Call Now"}
              </Button>
              <Text size="3" as="p" className="text-gray-500 mt-2">
                Schedule a call to discuss how these advisors can help you
              </Text>
            </div>
          </div>
        </div>
      </div>
    </Tooltip.Provider>
  );
};
